/**
 * VignanLogin
 * -------------------------------------------------------------------------
 * Vignan Student Portal — student login page (class components).
 *
 * A focused, split-screen student login:
 *   - Left  : Vignan branding (official logo), student-oriented highlights,
 *             and a student illustration.
 *   - Right : the login form (Username / Email + Password).
 *
 *   - Real GraphQL `login` mutation via react-apollo
 *   - Show / Hide password, Remember Me, Forgot Password
 *   - Validation messages, loading state, error handling
 *   - On success it redirects to /student/home
 *
 * Images are imported from src/images. Styling lives in ./vignanlogin.scss.
 * -------------------------------------------------------------------------
 */

import React, { Component } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { Link, withRouter } from "react-router-dom";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import { setSecureCookie } from "../../utils/secureCookie";

import vignanLogo from "../../images/vignan_logo.png";
import happyStudents from "../../images/happy-students.png";
import "./vignanlogin.scss";
import CommonHeader from '../components/navbars/CommonHeader';
import VIGNANLOGO from '../../images/vignan_short_logo.png';

const LOGIN_USER = gql`
  mutation($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      refreshToken
      user {
        username
        userlevel
        email
        valid
        name
        institution_id
        institute_name
      }
    }
  }
`;

/* ------------------------------------------------------------------ */
/* Validators                                                          */
/* ------------------------------------------------------------------ */

const validateUsername = (value = "") => {
  const v = value.trim();
  if (!v) return "Username is required";
  if (v.length < 3) return "Username must be at least 3 characters";
  return "";
};

const validatePassword = (value = "") => {
  if (!value) return "Password is required";
  if (value.length < 6) return "Password must be at least 6 characters";
  return "";
};

/* ------------------------------------------------------------------ */
/* Student-oriented highlights                                         */
/* ------------------------------------------------------------------ */

const HIGHLIGHTS = [
  { icon: "fa-chalkboard-teacher", text: "Attend live & recorded classes", color: "red" },
  { icon: "fa-file-alt", text: "Practice & take online exams", color: "blue" },
  { icon: "fa-chart-line", text: "Track your performance & rank", color: "purple" },
  { icon: "fa-book-open", text: "Access study material anytime", color: "amber" },
];

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

class VignanLogin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      values: { username: "", password: "" },
      touched: {},
      showPassword: false,
      remember: false,
      loading: false,
      submitError: "",
    };
  }

  componentDidMount() {
    if (Cookies.get("studenttoken")) {
      this.props.history.push("/student/home");
    }
  }

  getErrors() {
    const { values } = this.state;
    return {
      username: validateUsername(values.username),
      password: validatePassword(values.password),
    };
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      values: { ...prev.values, [name]: value },
      submitError: "",
    }));
  };

  handleBlur = (e) => {
    const { name } = e.target;
    this.setState((prev) => ({ touched: { ...prev.touched, [name]: true } }));
  };

  togglePassword = () =>
    this.setState((s) => ({ showPassword: !s.showPassword }));

  handleSubmit = (e) => {
    e.preventDefault();
    const errors = this.getErrors();
    const isValid = !errors.username && !errors.password;

    this.setState({ touched: { username: true, password: true } });
    if (!isValid) return;

    const { username, password } = this.state.values;
    this.setState({ loading: true, submitError: "" });

    this.login(username.trim(), password).catch((error) => {
      const message =
        (error.graphQLErrors &&
          error.graphQLErrors.map((x) => x.message).join(", ")) ||
        "Unable to login. Please check your credentials and try again.";
      this.setState({ submitError: message, loading: false });
    });
  };

  login = async (username, password) => {
    await this.props.login({
      variables: { username, password },
      update: (store, { data }) => {
        const { token, refreshToken, user } = data.login;

        setSecureCookie("token", token);
        setSecureCookie("studenttoken", token);
        setSecureCookie("refreshtoken", refreshToken);
        setSecureCookie("studentrefreshtoken", refreshToken);
        setSecureCookie("username", user.username);
        setSecureCookie("studentusername", user.name);
        setSecureCookie("name", user.name);
        // PII no longer stored client-side; the API returns it masked.
        // PII no longer stored client-side; the API returns it masked.
        setSecureCookie("student_userlevel", user.userlevel);
        setSecureCookie("user_valid", user.valid);
        setSecureCookie("institution_id", user.institution_id);
        setSecureCookie("institute_name", user.institute_name);
        setSecureCookie("toggle", "wrapper");

        if (data.login) {
          this.props.history.push("/student/home");
        }
      },
    });
  };

  render() {
    const { values, touched, showPassword, remember, loading, submitError } = this.state;
    const errors = this.getErrors();

    return (
      <React.Fragment>
      <CommonHeader color="auto" tone="light" showUserMenu={false} left={<img src={VIGNANLOGO} alt="Vignan" height="40" />} />
      <div className="vg-login">
        <div className="vg-shell">
          {/* ---------- Brand side ---------- */}
          <aside className="vg-brand">
            <img src={vignanLogo} alt="Vignan Junior College" className="vg-brand__logo" />

            <h1 className="vg-brand__title">Welcome to your Student Portal</h1>
            <p className="vg-brand__subtitle">
              Login to access your classes, exams, study material and real-time
              performance tracking.
            </p>

            <ul className="vg-brand__list">
              {HIGHLIGHTS.map((h) => (
                <li key={h.text}>
                  <span className={`vg-hl-ic vg-ic--${h.color}`}>
                    <i className={`fas ${h.icon}`} />
                  </span>
                  {h.text}
                </li>
              ))}
            </ul>

            <img src={happyStudents} alt="" aria-hidden="true" className="vg-brand__art" />
          </aside>

          {/* ---------- Login side ---------- */}
          <main className="vg-panel" aria-labelledby="vg-login-title">
            <div className="vg-panel__inner">
              <img src={vignanLogo} alt="Vignan Junior College" className="vg-panel__logo" />

              <h2 id="vg-login-title" className="vg-panel__title">
                Student <span>Login</span>
              </h2>
              <p className="vg-panel__subtitle">Sign in to continue to your account</p>

              <form className="vg-form" onSubmit={this.handleSubmit} noValidate>
                {submitError && (
                  <Alert variant="danger" className="vg-alert" role="alert">
                    <i className="fas fa-times-circle mr-2" />
                    {submitError}
                  </Alert>
                )}

                <div className="vg-field">
                  <label htmlFor="vg-username" className="vg-label">
                    <i className="far fa-user vg-label__ic" /> Username / Email
                  </label>
                  <div className={`vg-input ${touched.username && errors.username ? "is-invalid" : ""}`}>
                    <input
                      id="vg-username"
                      className="vg-input__control"
                      type="text"
                      name="username"
                      placeholder="Enter your username or email"
                      autoComplete="username"
                      value={values.username}
                      onChange={this.handleChange}
                      onBlur={this.handleBlur}
                      aria-invalid={touched.username && errors.username ? "true" : "false"}
                      aria-describedby={touched.username && errors.username ? "vg-username-error" : undefined}
                    />
                  </div>
                  {touched.username && errors.username && (
                    <span className="vg-error" id="vg-username-error" role="alert">
                      <i className="fas fa-exclamation-circle" /> {errors.username}
                    </span>
                  )}
                </div>

                <div className="vg-field">
                  <label htmlFor="vg-password" className="vg-label">
                    <i className="fas fa-lock vg-label__ic" /> Password
                  </label>
                  <div className={`vg-input ${touched.password && errors.password ? "is-invalid" : ""}`}>
                    <input
                      id="vg-password"
                      className="vg-input__control"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      value={values.password}
                      onChange={this.handleChange}
                      onBlur={this.handleBlur}
                      aria-invalid={touched.password && errors.password ? "true" : "false"}
                      aria-describedby={touched.password && errors.password ? "vg-password-error" : undefined}
                    />
                    <button
                      type="button"
                      className="vg-input__toggle"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      onClick={this.togglePassword}
                    >
                      <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <span className="vg-error" id="vg-password-error" role="alert">
                      <i className="fas fa-exclamation-circle" /> {errors.password}
                    </span>
                  )}
                </div>

                <div className="vg-form__meta">
                  <Form.Check
                    type="checkbox"
                    id="vg-remember"
                    label="Remember me"
                    checked={remember}
                    onChange={(e) => this.setState({ remember: e.target.checked })}
                  />
                  <Link to="/student/ForgotPassword" className="vg-link">
                    Forgot Password?
                  </Link>
                </div>

                <Button type="submit" className="vg-submit" disabled={loading} block>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-2" /> Signing in…
                    </>
                  ) : (
                    <>
                      Login <i className="fas fa-arrow-right ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="vg-panel__contact">
                Need help? <i className="fas fa-phone-alt" />{" "}
                <a href="tel:+919063148877">+91 906 314 8877</a>
              </p>
            </div>
          </main>
        </div>
      </div>
      </React.Fragment>
    );
  }
}

export default withRouter(
  graphql(LOGIN_USER, {
    name: "login",
  })(VignanLogin)
);
